import type {
  AnalyticsBrowser,
  EventProperties,
  GroupTraits,
  ID,
  LegacySettings,
  Options,
  SegmentEvent,
} from "@segment/analytics-next"
import * as Sentry from "@sentry/browser"
import type { Mixpanel } from "mixpanel-browser"
import { unifyAnonymousIds } from "./util/marketing"
import type { Config } from "@ironzion/frontend-js-config"

// TODO: don't expose this on window; pass it around to the ports which need it
declare global {
  interface Window {
    analytics: AnalyticsBrowser | AnalyticsShim

    // types for things that Segment (probably) loads
    mixpanel?: Mixpanel
  }
}

type Call =
  | { method: "track"; event: string | SegmentEvent; properties: EventProperties; context: Options }
  | { method: "identify"; id: object | ID; traits: GroupTraits }
  | { method: "group"; id: object | ID; traits: GroupTraits }
  | { method: "alias"; to: string | number; from: string | number | Options }
  | { method: "page"; category: string | object; name: string | object; properties: EventProperties; options: Options }
  | { method: "reset" }

class AnalyticsShim {
  calls: Call[] = []

  track(event: string | SegmentEvent, properties: EventProperties, context: Options) {
    console.debug(`buffering track "${event}"`)
    this.calls.push({ method: "track", event, properties, context })
  }

  identify(id: object | ID, traits: GroupTraits) {
    console.debug(`buffering identify "${id}"`)
    this.calls.push({ method: "identify", id, traits })
  }

  group(id: object | ID, traits: GroupTraits) {
    console.debug(`buffering group "${id}"`)
    this.calls.push({ method: "group", id, traits })
  }

  alias(to: string | number, from: string | number | Options) {
    console.debug(`buffering alias "${to}" from "${from}"`)
    this.calls.push({ method: "alias", to, from })
  }

  page(category: string | object, name: string | object, properties: EventProperties, options: Options) {
    console.debug(`buffering page "${category}" "${name}"`)
    this.calls.push({ method: "page", category, name, properties, options })
  }

  reset() {
    console.debug("buffering reset")
    this.calls.push({ method: "reset" })
  }
}

window.analytics = new AnalyticsShim()

/**
 * @param config - global config to source keys from
 */
export async function configureSegment(config: Config): Promise<void> {
  const { AnalyticsBrowser, loadLegacySettings } = await import("@segment/analytics-next")

  const oldAnalytics = window.analytics

  // We have a funny problem with our analytics config: by default, when
  // Segment loads it's actually just setting up a shim. It won't actually load
  // destinations or make any network calls until we call the first
  // track/identify/whatever method on it. That wouldn't be a problem except
  // for three things:
  //
  // 1. Part of the initialization is getting the settings for our key, which
  //    makes a network request to cdn.segment.com
  // 2. Firefox and Safari both have an enhanced privacy mode that will block
  //    all network traffic to cdn.segment.com with CORS
  // 3. They use promises in a blocking context to do this, so the promise
  //    escapes in a way that can't be caught with try..catch either down with
  //    `load` or at the callsites that actually trigger the error.
  //
  // The combination of all of the above means that when Firefox and Safari's
  // enhanced privacy modes are turned on, the first call to Segment will THROW
  // AN UNCATCHABLE EXCEPTION (they leak a promise somewhere.) Since we ask for
  // the Segment user as part of starting the app, that means that this
  // uncatchable exception will break the page completely.
  //
  // As you might be able to infer from the size of this comment block,
  // figuring all that out was really annoying. Fortunately, there's a
  // workaround: Segment knows that the requests might get blocked and lets you
  // specify your own integration settings. Usually people use this for
  // implementing their own analytics proxy to get around blocklists, but you
  // can just use the settings thaty provide too (which the client would get
  // anyway for us, but lazily.) Even better, if you do this manually, you can
  // do it in a context where we can actually catch exceptions related to
  // blocklists.
  //
  // This is probably safe long-term: it would be really silly of Segment to go
  // around the CDN proxies that they allow their customers to set up. If they
  // do, it's definitely worth annoying them about fixing.
  let settings: LegacySettings
  try {
    settings = await loadLegacySettings(config.segmentKey)
  } catch (err) {
    // If the call to Segment is being blocked by Firefox or Safari's privacy
    // tools, the exception will be a CORS error, so we won't be able to get
    // any details out of it as a security measure. All we can see is that the
    // message will say "NetworkError".
    if (err.message.match(/NetworkError/)) {
      console.log("Could not load Segment settings (cdn.segment.com is probably blocked)")
      return
    }

    // If it's anything but a network error, re-raise the exception. The scope
    // that calls this is expected to handle exceptions properly.
    throw err
  }

  window.analytics = AnalyticsBrowser.load({
    writeKey: config.segmentKey,
    // The type here is a little silly: we want to make sure that we never pass
    // in the wrong type and break our integration on upgrades, but
    // `loadLegacySettings` only returns a `LegacySettings` when `cdnSettings`
    // expects that you can add your own settings as well.
    cdnSettings: settings as typeof settings & Record<string, unknown>,
  })

  if (oldAnalytics instanceof AnalyticsShim) {
    for (const call of oldAnalytics.calls) {
      switch (call.method) {
        case "track":
          await window.analytics.track(call.event, call.properties, call.context)
          break

        case "identify":
          await window.analytics.identify(call.id, call.traits)
          break

        case "group":
          await window.analytics.group(call.id, call.traits)
          break

        case "alias":
          await window.analytics.alias(call.to, call.from)
          break

        case "page":
          await window.analytics.page(call.category, call.name, call.properties, call.options)
          break

        case "reset":
          await window.analytics.reset()
          break

        default:
          const exhaustivelyMatched: never = call
          Sentry.captureException(new Error(`unhandled analytics method ${exhaustivelyMatched}`))
          break
      }
    }
  }

  await unifyAnonymousIds(window.analytics)
}

declare global {
  interface Window {
    _fs_run_in_iframe?: boolean | undefined
  }
}
